import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { X, Save, Search, Building2, Plus, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { Country, State, City } from 'country-state-city';
import LossReasonModal from './LossReasonModal';

const DOCUMENT_TYPES = ['NIT', 'CC', 'CE'];  // NIT, Cédula, Cédula Extranjería
const PERSON_TYPES = ['Persona Natural', 'Persona Jurídica'];
const CHANNEL_OPTIONS = [
  'Referido',
  'Recompra',
  'Sitio Web',
  'Redes Sociales',
  'Google Ads',
  'Facebook Ads',
  'LinkedIn',
  'Evento/Feria',
  'Llamada Fría',
  'Email Marketing',
  'Alianza Estratégica',
  'WhatsApp',
  'Instagram',
  'TikTok',
  'YouTube',
  'Otro'
];

// Códigos de país
const COUNTRY_CODES = [
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+1', country: 'USA/Canadá', flag: '🇺🇸' },
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+55', country: 'Brasil', flag: '🇧🇷' },
  { code: '+507', country: 'Panamá', flag: '🇵🇦' },
  { code: '+506', country: 'Costa Rica', flag: '🇨🇷' },
  { code: '+502', country: 'Guatemala', flag: '🇬🇹' },
  { code: '+503', country: 'El Salvador', flag: '🇸🇻' },
  { code: '+504', country: 'Honduras', flag: '🇭🇳' },
  { code: '+505', country: 'Nicaragua', flag: '🇳🇮' },
  { code: '+591', country: 'Bolivia', flag: '🇧🇴' },
  { code: '+595', country: 'Paraguay', flag: '🇵🇾' },
  { code: '+598', country: 'Uruguay', flag: '🇺🇾' },
  { code: '+44', country: 'Reino Unido', flag: '🇬🇧' },
  { code: '+33', country: 'Francia', flag: '🇫🇷' },
  { code: '+49', country: 'Alemania', flag: '🇩🇪' },
  { code: '+39', country: 'Italia', flag: '🇮🇹' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
];

const LeadFormModal = ({ isOpen, onClose, onSuccess, leadToEdit = null, pipelineId = null, stageId = null }) => {
  const { state, actions } = useData();
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState('+57');
  
  // Estado para búsqueda de empresas
  const [empresaSearch, setEmpresaSearch] = useState('');
  const [empresaResults, setEmpresaResults] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [showEmpresaDropdown, setShowEmpresaDropdown] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  
  // Estado para búsqueda de país y ciudad
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  
  // Track if form was modified - for cancellation handling
  const [formInitialized, setFormInitialized] = useState(false);
  
  // Loss reason modal state
  const [showLossReasonModal, setShowLossReasonModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  
  // Campaign tags list (available campaigns)
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  // Selected campaign tags for the lead (new format: array of {tag, addedAt, name, channel})
  const [selectedCampaignTags, setSelectedCampaignTags] = useState([]);
  
  // Fetch available campaigns
  useEffect(() => {
    if (!isOpen) return;
    const fetchTags = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/tags-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setAvailableCampaigns(await res.json());
      } catch {}
    };
    fetchTags();
  }, [isOpen]);
  
  const [formData, setFormData] = useState({
    // Campos base
    name: '',
    email: '',
    phone: '',
    empresaId: '',
    company: '',
    documentType: '',
    documentNumber: '',
    personType: '',
    country: 'CO',  // ISO code for Colombia
    city: 'Bogotá D.C.',
    value: '',
    pipelineId: pipelineId || state.pipelines[0]?.id || '',
    stageId: stageId || state.pipelines[0]?.stages?.[0]?.id || '',
    priority: 'Media',
    channel: '', // Canal de origen
    campaignTags: [], // Campaign tags array (new format)
    notes: '',
    // Campos privados (no públicos)
    closedValue: '',
    closedDate: '',
    // Campos personalizados
    customFields: {},
  });

  // Reset all form state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset form to prevent stale data on next open
      setFormInitialized(false);
      setEmpresaSearch('');
      setEmpresaResults([]);
      setSelectedEmpresa(null);
      setShowEmpresaDropdown(false);
      // Reset country/city search
      setCountrySearch('');
      setCitySearch('');
      setShowCountryDropdown(false);
      setShowCityDropdown(false);
      // Reset campaign tags
      setSelectedCampaignTags([]);
    }
  }, [isOpen]);

  // Initialize pipeline/stage when pipelines load
  useEffect(() => {
    if (!isOpen) return; // Don't initialize if modal is closed
    if (state.pipelines.length > 0 && !formData.pipelineId) {
      const pipeline = state.pipelines[0];
      setFormData(prev => ({
        ...prev,
        pipelineId: pipeline.id,
        stageId: pipeline?.stages?.[0]?.id || '',
      }));
    }
  }, [state.pipelines, formData.pipelineId, isOpen]);

  // Load data when editing - only when modal opens
  useEffect(() => {
    if (!isOpen) return; // Don't load if modal is closed
    if (formInitialized) return; // Don't reload if already initialized
    
    if (leadToEdit) {
      // Extraer código de país del teléfono si existe
      let phone = leadToEdit.phone || '';
      let extractedCode = '+57';
      
      // Buscar si el teléfono comienza con un código de país conocido
      for (const country of COUNTRY_CODES) {
        if (phone.startsWith(country.code)) {
          extractedCode = country.code;
          phone = phone.replace(country.code, '').trim();
          break;
        }
      }
      
      setCountryCode(extractedCode);
      
      // IMPORTANT: Preserve the lead's existing pipeline/stage values first
      // Only fallback to defaults if the lead doesn't have values set
      const leadPipelineId = leadToEdit.pipelineId || '';
      const leadStageId = leadToEdit.stageId || '';
      
      // Only use defaults if lead has no pipeline assigned AND pipelines are available
      let effectivePipelineId = leadPipelineId;
      let effectiveStageId = leadStageId;
      
      if (!leadPipelineId && state.pipelines.length > 0) {
        const defaultPipeline = state.pipelines[0];
        effectivePipelineId = defaultPipeline?.id || '';
        effectiveStageId = defaultPipeline?.stages?.[0]?.id || '';
      }
      
      setFormData({
        name: leadToEdit.name || '',
        email: leadToEdit.email || '',
        phone: phone,
        company: leadToEdit.company || '',
        empresaId: leadToEdit.empresaId || '',
        documentType: leadToEdit.documentType || '',
        documentNumber: leadToEdit.documentNumber || '',
        personType: leadToEdit.personType || '',
        country: (() => {
          // If country is already an ISO code (2 letters), use it directly
          if (leadToEdit.country && leadToEdit.country.length === 2) {
            return leadToEdit.country;
          }
          // Otherwise, try to find the country by name and get its ISO code
          const foundCountry = Country.getAllCountries().find(
            c => c.name.toLowerCase() === (leadToEdit.country || '').toLowerCase()
          );
          return foundCountry?.isoCode || 'CO';
        })(),
        city: leadToEdit.city || 'Bogotá D.C.',
        value: leadToEdit.value || '',
        pipelineId: effectivePipelineId,
        stageId: effectiveStageId,
        priority: leadToEdit.priority || 'Media',
        channel: leadToEdit.trackingData?.channel || leadToEdit.source || '',
        campaignTags: leadToEdit.campaignTags || [],
        notes: leadToEdit.notes || '',
        closedValue: leadToEdit.closedValue || '',
        closedDate: leadToEdit.closedDate || '',
        customFields: leadToEdit.customFields || {},
      });
      
      // Load campaign tags for editing (handle both old and new format)
      const loadCampaignTags = async () => {
        // If lead has new format campaignTags array
        if (leadToEdit.campaignTags && leadToEdit.campaignTags.length > 0) {
          // Enrich with campaign names from availableCampaigns
          const enrichedTags = leadToEdit.campaignTags.map(ct => {
            const campaign = availableCampaigns.find(c => c.tag === ct.tag);
            return {
              ...ct,
              name: campaign?.name || ct.tag,
              channel: campaign?.channel || ''
            };
          });
          setSelectedCampaignTags(enrichedTags);
        } 
        // If lead has old format campaignTag string
        else if (leadToEdit.campaignTag) {
          const campaign = availableCampaigns.find(c => c.tag === leadToEdit.campaignTag);
          setSelectedCampaignTags([{
            tag: leadToEdit.campaignTag,
            addedAt: leadToEdit.createdAt,
            name: campaign?.name || leadToEdit.campaignTag,
            channel: campaign?.channel || ''
          }]);
        } else {
          setSelectedCampaignTags([]);
        }
      };
      loadCampaignTags();
      
      // Set country and city search values
      const countryCode = (() => {
        if (leadToEdit.country && leadToEdit.country.length === 2) {
          return leadToEdit.country;
        }
        const foundCountry = Country.getAllCountries().find(
          c => c.name.toLowerCase() === (leadToEdit.country || '').toLowerCase()
        );
        return foundCountry?.isoCode || 'CO';
      })();
      const countryData = Country.getCountryByCode(countryCode);
      if (countryData) {
        setCountrySearch(countryData.name);
      }
      if (leadToEdit.city) {
        setCitySearch(leadToEdit.city);
      }
      
      // Mark form as initialized to prevent re-initialization
      setFormInitialized(true);
      
      // If lead has empresaId, load the empresa and set it as selected
      if (leadToEdit.empresaId) {
        const loadEmpresa = async () => {
          try {
            const result = await import('../services/api').then(api => api.searchEmpresas(leadToEdit.company || '', 10));
            const empresa = result.empresas?.find(e => e.id === leadToEdit.empresaId);
            if (empresa) {
              setSelectedEmpresa(empresa);
              setEmpresaSearch(empresa.name);
            }
          } catch (error) {
            console.error('Error loading empresa:', error);
          }
        };
        loadEmpresa();
      } else if (leadToEdit.company) {
        // If has company name but no empresaId, try to find and select the empresa
        const findEmpresa = async () => {
          try {
            const result = await import('../services/api').then(api => api.searchEmpresas(leadToEdit.company, 10));
            const empresa = result.empresas?.find(e => e.name.toLowerCase() === leadToEdit.company.toLowerCase());
            if (empresa) {
              setSelectedEmpresa(empresa);
              setEmpresaSearch(empresa.name);
              setFormData(prev => ({
                ...prev,
                empresaId: empresa.id,
                documentType: empresa.documentType || prev.documentType,
                documentNumber: empresa.documentNumber || prev.documentNumber,
              }));
            } else {
              // No matching empresa found, just show in search
              setEmpresaSearch(leadToEdit.company);
            }
          } catch (error) {
            console.error('Error finding empresa:', error);
            setEmpresaSearch(leadToEdit.company);
          }
        };
        findEmpresa();
      }
    } else if (pipelineId) {
      const pipeline = state.pipelines.find(p => p.id === pipelineId);
      setFormData(prev => ({
        ...prev,
        pipelineId: pipelineId,
        stageId: stageId || pipeline?.stages?.[0]?.id || '',
      }));
      // Set default country/city search for new lead
      setCountrySearch('Colombia');
      setCitySearch('Bogotá D.C.');
      setFormInitialized(true);
    } else {
      // New lead without preset pipeline - mark as initialized after pipelines load
      if (state.pipelines.length > 0) {
        // Set default country/city search for new lead
        if (!countrySearch) {
          setCountrySearch('Colombia');
          setCitySearch('Bogotá D.C.');
        }
        setFormInitialized(true);
      }
    }
  }, [leadToEdit, pipelineId, stageId, state.pipelines, isOpen, formInitialized]);

  // Buscar empresas cuando cambia el texto de búsqueda
  useEffect(() => {
    const searchEmpresas = async () => {
      // Si el dropdown no está visible, no buscar
      if (!showEmpresaDropdown) return;
      
      setLoadingEmpresas(true);
      try {
        // Buscar empresas - si no hay query, traer todas las primeras 10
        const result = await import('../services/api').then(api => api.searchEmpresas(empresaSearch, 10));
        setEmpresaResults(result.empresas || []);
      } catch (error) {
        console.error('Error searching empresas:', error);
      } finally {
        setLoadingEmpresas(false);
      }
    };

    const debounce = setTimeout(searchEmpresas, 200);
    return () => clearTimeout(debounce);
  }, [empresaSearch, showEmpresaDropdown]);

  // Cargar empresas cuando se abre el dropdown (siempre cargar al hacer foco)
  const handleEmpresaFocus = async () => {
    setShowEmpresaDropdown(true);
    // Siempre cargar empresas al hacer foco para mostrar el listado
    setLoadingEmpresas(true);
    try {
      const result = await import('../services/api').then(api => api.searchEmpresas(empresaSearch, 10));
      setEmpresaResults(result.empresas || []);
    } catch (error) {
      console.error('Error loading empresas:', error);
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const handleSelectEmpresa = (empresa) => {
    setSelectedEmpresa(empresa);
    setEmpresaSearch(empresa.name);
    setFormData(prev => ({
      ...prev,
      empresaId: empresa.id,
      company: empresa.name,
      // Copy document info from empresa
      documentType: empresa.documentType || prev.documentType,
      documentNumber: empresa.documentNumber || prev.documentNumber,
      personType: empresa.documentType === 'NIT' ? 'Persona Jurídica' : (empresa.personType || prev.personType),
    }));
    setShowEmpresaDropdown(false);
  };

  const handleClearEmpresa = () => {
    setSelectedEmpresa(null);
    setEmpresaSearch('');
    setFormData(prev => ({
      ...prev,
      empresaId: '',
      company: '',
      // Clear document info when removing empresa
      documentType: '',
      documentNumber: '',
      personType: '',
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Auto-assign person type based on document type
    if (name === 'documentType') {
      const personType = value === 'NIT' ? 'Persona Jurídica' : 'Persona Natural';
      setFormData(prev => ({
        ...prev,
        documentType: value,
        personType: personType,
      }));
      return;
    }
    
    // Clean whitespace from email and phone fields
    let cleanedValue = value;
    if (name === 'email') {
      cleanedValue = value.replace(/\s/g, ''); // Remove all whitespace from email
    } else if (name === 'phone') {
      cleanedValue = value.replace(/\s/g, ''); // Remove all whitespace from phone
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : cleanedValue,
    }));
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [fieldId]: value,
      },
    }));
  };

  const handlePipelineChange = (e) => {
    const newPipelineId = e.target.value;
    const pipeline = state.pipelines.find(p => p.id === newPipelineId);
    setFormData(prev => ({
      ...prev,
      pipelineId: newPipelineId,
      stageId: pipeline?.stages[0]?.id || '',
      customFields: {}, // Reset custom fields when pipeline changes
    }));
  };

  const handleSubmit = async (e, lossReason = null) => {
    e?.preventDefault?.();

    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('El correo es requerido');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('El celular es requerido');
      return;
    }
    
    // Validar formato de documento solo si se proporcionó
    if (formData.documentType && formData.documentNumber.trim()) {
      const docNumber = formData.documentNumber.trim();
      if (formData.documentType === 'NIT') {
        // NIT puede tener formato: solo números o números-dígito verificación (ej: 900123456-7)
        if (docNumber && !/^\d+(-\d)?$/.test(docNumber)) {
          toast.error('El NIT debe contener solo dígitos numéricos (opcionalmente con dígito de verificación: 123456789-0)');
          return;
        }
      } else if (formData.documentType === 'CC') {
        if (!/^\d{8,10}$/.test(docNumber)) {
          toast.error('La cédula debe contener entre 8 y 10 dígitos numéricos');
          return;
        }
      }
    }

    // Check if editing and stage changed to a "perdido" stage - show loss reason modal
    // Detect by ID pattern or stage name
    const currentPipeline = state.pipelines.find(p => p.id === formData.pipelineId);
    const targetStage = currentPipeline?.stages?.find(s => s.id === formData.stageId);
    const oldStage = currentPipeline?.stages?.find(s => s.id === leadToEdit?.stageId);
    
    const isTargetLostStage = formData.stageId === 'perdido' || 
                               formData.stageId?.includes('perdido') || 
                               targetStage?.name?.toLowerCase() === 'perdido';
    
    const wasAlreadyLost = leadToEdit?.stageId === 'perdido' || 
                           leadToEdit?.stageId?.includes('perdido') || 
                           oldStage?.name?.toLowerCase() === 'perdido';
    
    const isChangingToPerdido = leadToEdit && 
                                isTargetLostStage && 
                                !wasAlreadyLost &&
                                !lossReason;
    
    if (isChangingToPerdido) {
      // Store form data and show loss reason modal
      setPendingFormData({ ...formData });
      setShowLossReasonModal(true);
      return;
    }

    // Combinar código de país con el número de teléfono
    const fullPhone = formData.phone ? `${countryCode} ${formData.phone}`.trim() : '';

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: fullPhone,
        company: formData.company,
        empresaId: formData.empresaId || null, // Include empresaId
        documentType: formData.documentType,
        documentNumber: formData.documentNumber,
        personType: formData.personType,
        country: Country.getCountryByCode(formData.country)?.name || formData.country || 'Colombia',
        city: formData.city || 'Bogotá',
        value: formData.value ? parseFloat(formData.value) : 0,
        pipelineId: formData.pipelineId,
        stageId: formData.stageId,
        priority: formData.priority,
        source: formData.channel || 'Manual',
        notes: formData.notes,
        // Campos privados (no públicos)
        closedValue: formData.closedValue ? parseFloat(formData.closedValue) : null,
        closedDate: formData.closedDate || null,
        customFields: formData.customFields,
        data: formData.customFields,
        // Agregar canal al trackingData
        trackingData: {
          channel: formData.channel || 'Manual',
        },
        // Campaign tags (new format: array with timestamps)
        campaignTags: selectedCampaignTags.map(ct => ({
          tag: ct.tag,
          addedAt: ct.addedAt || new Date().toISOString()
        })),
      };

      // Add loss reason data if provided
      if (lossReason) {
        leadData.lossReason = lossReason.reasonId;
        leadData.lossReasonLabel = lossReason.reasonLabel;
        leadData.lossReasonNote = lossReason.customNote;
        leadData.lostAt = new Date().toISOString();
      }

      if (leadToEdit) {
        const updatedLead = await actions.updateLead(leadToEdit.id, leadData);
        if (lossReason) {
          toast.success('Lead marcado como perdido');
        } else {
          toast.success('Lead actualizado exitosamente');
        }
        // Call onSuccess with updated lead data
        if (onSuccess && updatedLead) {
          onSuccess(updatedLead);
        }
      } else {
        const newLead = await actions.addLead(leadData);
        
        await actions.addActivity({
          leadId: newLead.id,
          type: 'note',
          description: 'Lead creado',
        });

        toast.success('Lead creado exitosamente');
        navigate(`/lead/${newLead.id}`);
      }

      onClose();
    } catch (error) {
      // Show backend error message if available
      const errorMessage = error?.response?.data?.detail || 'Error al guardar el lead';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  // Handle loss reason confirmation
  const handleLossReasonConfirm = async (lossReason) => {
    setShowLossReasonModal(false);
    // Call handleSubmit with the loss reason
    await handleSubmit(null, lossReason);
    setPendingFormData(null);
  };

  // Handle loss reason cancel
  const handleLossReasonCancel = () => {
    setShowLossReasonModal(false);
    setPendingFormData(null);
    // Revert stage to original
    if (leadToEdit) {
      setFormData(prev => ({
        ...prev,
        stageId: leadToEdit.stageId
      }));
    }
  };

  if (!isOpen) return null;

  const selectedPipeline = state.pipelines.find(p => p.id === formData.pipelineId);
  // Only show active custom fields
  const customFields = (selectedPipeline?.customFields || []).filter(f => f.isActive !== false);

  // Render custom field based on type
  const renderCustomField = (field) => {
    const value = formData.customFields[field.id] || '';
    const options = Array.isArray(field.options) ? field.options : [];
    
    switch (field.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={field.required}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
          >
            <option value="">Seleccionar...</option>
            {options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={field.required}
            rows={3}
            placeholder={field.placeholder || field.label}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 resize-none"
          />
        );
      
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={value === true || value === 'true'}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.checked)}
            className="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
          />
        );
      
      case 'radio':
        return (
          <div className="flex flex-wrap gap-4">
            {options.map(opt => (
              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={field.id}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                  required={field.required}
                  className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-600"
                />
                <span className="text-sm text-slate-700">{opt}</span>
              </label>
            ))}
          </div>
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={field.required}
            placeholder={field.placeholder || field.label}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={field.required}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
          />
        );
      
      default:
        return (
          <input
            type={field.type || 'text'}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={field.required}
            placeholder={field.placeholder || field.label}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
          />
        );
    }
  };

  // Don't render anything if modal is closed
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-slate-900">
            {leadToEdit ? 'Editar Lead' : 'Nuevo Lead'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Section: Información de Contacto */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
              Información de Contacto
            </h3>
            <div className="space-y-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nombre Contacto <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  data-testid="lead-name-input"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  placeholder="Nombre completo"
                />
              </div>

              {/* Email y Teléfono */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Correo <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    data-testid="lead-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="correo@ejemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Celular <span className="text-red-600">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="w-28 px-2 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 text-sm"
                    >
                      {COUNTRY_CODES.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      data-testid="lead-phone-input"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="300 123 4567"
                    />
                  </div>
                </div>
              </div>

              {/* Empresa - Buscador */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Empresa
                </label>
                <div className="relative">
                  {selectedEmpresa ? (
                    <div className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-indigo-600" />
                        <div>
                          <p className="font-medium text-slate-900">{selectedEmpresa.name}</p>
                          {selectedEmpresa.documentNumber && (
                            <p className="text-xs text-slate-500">{selectedEmpresa.documentType}: {selectedEmpresa.documentNumber}</p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleClearEmpresa}
                        className="p-1 text-slate-400 hover:text-red-600 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={empresaSearch}
                          onChange={(e) => {
                            setEmpresaSearch(e.target.value);
                            setShowEmpresaDropdown(true);
                          }}
                          onFocus={handleEmpresaFocus}
                          onBlur={() => setTimeout(() => setShowEmpresaDropdown(false), 200)}
                          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                          placeholder="Clic para ver empresas o escribe para buscar..."
                        />
                        {loadingEmpresas && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                        )}
                      </div>
                      
                      {/* Dropdown de resultados */}
                      {showEmpresaDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {loadingEmpresas ? (
                            <div className="p-4 text-center text-sm text-slate-500">
                              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                              Cargando empresas...
                            </div>
                          ) : empresaResults.length > 0 ? (
                            empresaResults.map(empresa => (
                              <button
                                key={empresa.id}
                                type="button"
                                onClick={() => handleSelectEmpresa(empresa)}
                                className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-0"
                              >
                                <Building2 className="w-4 h-4 text-slate-400" />
                                <div>
                                  <p className="font-medium text-slate-900">{empresa.name}</p>
                                  {empresa.documentNumber && (
                                    <p className="text-xs text-slate-500">{empresa.documentType}: {empresa.documentNumber}</p>
                                  )}
                                </div>
                              </button>
                            ))
                          ) : empresaSearch.length > 0 ? (
                            <div className="p-4 text-center">
                              <p className="text-sm text-slate-500 mb-3">No se encontraron empresas con "{empresaSearch}"</p>
                              <Link
                                to="/empresas"
                                onClick={onClose}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                              >
                                <Plus className="w-4 h-4" />
                                Crear Nueva Empresa
                              </Link>
                            </div>
                          ) : (
                            <div className="p-4 text-center">
                              <p className="text-sm text-slate-500 mb-3">No hay empresas registradas</p>
                              <Link
                                to="/empresas"
                                onClick={onClose}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                              >
                                <Plus className="w-4 h-4" />
                                Crear Primera Empresa
                              </Link>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {!selectedEmpresa && "Busca y selecciona una empresa existente o "}
                  <Link to="/empresas" onClick={onClose} className="text-indigo-600 hover:underline">
                    crea una nueva
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Section: Información del Embudo */}
          <div className="mb-6 pt-6 border-t border-slate-200">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
              Información del Embudo
            </h3>
            <div className="space-y-4">
              {/* Pipeline y Etapa */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Embudo
                  </label>
                  <select
                    name="pipelineId"
                    data-testid="lead-pipeline-select"
                    value={formData.pipelineId}
                    onChange={handlePipelineChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  >
                    <option value="">Seleccionar embudo...</option>
                    {state.pipelines.length === 0 && formData.pipelineId && (
                      <option value={formData.pipelineId}>
                        {leadToEdit?.pipelineName || 'Cargando embudos...'}
                      </option>
                    )}
                    {state.pipelines.map(pipeline => (
                      <option key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Etapa
                  </label>
                  <select
                    name="stageId"
                    data-testid="lead-stage-select"
                    value={formData.stageId}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  >
                    <option value="">Seleccionar etapa...</option>
                    {(!selectedPipeline?.stages || selectedPipeline.stages.length === 0) && formData.stageId && (
                      <option value={formData.stageId}>
                        {leadToEdit?.stageName || 'Cargando etapas...'}
                      </option>
                    )}
                    {selectedPipeline?.stages?.map(stage => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Prioridad y Canal de Origen */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Prioridad
                  </label>
                  <select
                    name="priority"
                    data-testid="lead-priority-select"
                    value={formData.priority}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  >
                    <option value="Alta">Alta</option>
                    <option value="Media">Media</option>
                    <option value="Baja">Baja</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Canal de Origen
                  </label>
                  <select
                    name="channel"
                    data-testid="lead-channel-select"
                    value={formData.channel}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  >
                    <option value="">Seleccionar canal...</option>
                    {CHANNEL_OPTIONS.map(channel => (
                      <option key={channel} value={channel}>{channel}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Campaña (Single Selection) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Campaña
                </label>
                <div data-testid="lead-campaigns-selector">
                  {/* Show selected campaign as chip */}
                  {selectedCampaignTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedCampaignTags.map((tagInfo) => (
                        <div
                          key={tagInfo.tag}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border bg-indigo-100 text-indigo-700 border-indigo-200"
                        >
                          <span className="font-medium">{tagInfo.name || tagInfo.tag}</span>
                          {tagInfo.addedAt && (
                            <span className="text-xs opacity-75 ml-1 border-l pl-1.5 border-current/20">
                              {new Date(tagInfo.addedAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setSelectedCampaignTags([])}
                            className="ml-1 p-0.5 hover:bg-black/10 rounded-full transition-colors"
                            aria-label={`Quitar campaña ${tagInfo.name || tagInfo.tag}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Campaign selector - only show if none selected */}
                  {selectedCampaignTags.length === 0 && availableCampaigns.length > 0 && (
                    <select
                      onChange={(e) => {
                        const tag = e.target.value;
                        if (!tag) return;
                        const campaign = availableCampaigns.find(c => c.tag === tag);
                        if (campaign) {
                          setSelectedCampaignTags([{
                            tag: campaign.tag,
                            addedAt: new Date().toISOString(),
                            name: campaign.name,
                            channel: campaign.channel
                          }]);
                        }
                        e.target.value = '';
                      }}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 text-slate-600"
                      defaultValue=""
                    >
                      <option value="">Seleccionar campaña...</option>
                      {availableCampaigns.map(c => (
                        <option key={c.tag} value={c.tag}>{c.name} ({c.channel})</option>
                      ))}
                    </select>
                  )}
                  
                  {availableCampaigns.length === 0 && selectedCampaignTags.length === 0 && (
                    <p className="text-sm text-slate-500">No hay campañas disponibles</p>
                  )}
                </div>
              </div>

              {/* País y Ciudad */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* País con búsqueda */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    País
                  </label>
                  <input
                    type="text"
                    data-testid="lead-country-search"
                    value={countrySearch || (formData.country ? Country.getCountryByCode(formData.country)?.name || '' : '')}
                    onChange={(e) => {
                      setCountrySearch(e.target.value);
                      setShowCountryDropdown(true);
                      if (!e.target.value) {
                        setFormData(prev => ({ ...prev, country: '', city: '' }));
                        setCitySearch('');
                      }
                    }}
                    onFocus={() => setShowCountryDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                    placeholder="Buscar país..."
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                  {showCountryDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {Country.getAllCountries()
                        .filter(c => 
                          !countrySearch || 
                          c.name.toLowerCase().includes(countrySearch.toLowerCase())
                        )
                        .slice(0, 50)
                        .map(country => (
                          <button
                            key={country.isoCode}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, country: country.isoCode, city: '' }));
                              setCountrySearch(country.name);
                              setCitySearch('');
                              setShowCountryDropdown(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-slate-50 text-sm"
                          >
                            {country.flag} {country.name}
                          </button>
                        ))
                      }
                    </div>
                  )}
                </div>

                {/* Ciudad con búsqueda */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Ciudad
                  </label>
                  <input
                    type="text"
                    data-testid="lead-city-search"
                    value={citySearch || formData.city || ''}
                    onChange={(e) => {
                      setCitySearch(e.target.value);
                      setFormData(prev => ({ ...prev, city: e.target.value }));
                      setShowCityDropdown(true);
                    }}
                    onFocus={() => setShowCityDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCityDropdown(false), 200)}
                    placeholder="Buscar ciudad..."
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                  {showCityDropdown && formData.country && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {(() => {
                        // Get unique cities (remove duplicates)
                        const allCities = City.getCitiesOfCountry(formData.country) || [];
                        const uniqueCities = [...new Map(allCities.map(c => [c.name, c])).values()];
                        
                        return uniqueCities
                          .filter(c => 
                            !citySearch || 
                            c.name.toLowerCase().includes(citySearch.toLowerCase())
                          )
                          .slice(0, 50)
                          .map((city, index) => (
                            <button
                              key={`${city.name}-${index}`}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, city: city.name }));
                                setCitySearch(city.name);
                                setShowCityDropdown(false);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-slate-50 text-sm"
                            >
                              {city.name}
                            </button>
                          ));
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section: Campos Personalizados */}
          {customFields.length > 0 && (
            <div className="mb-6 pt-6 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
                Información Adicional ({selectedPipeline?.name})
              </h3>
              <div className="space-y-4">
                {customFields.map(field => (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {field.label} {field.required && <span className="text-red-600">*</span>}
                    </label>
                    {renderCustomField(field)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              <Save className="w-4 h-4" />
              {leadToEdit ? 'Actualizar Lead' : 'Crear Lead'}
            </button>
          </div>
        </form>
      </div>

      {/* Loss Reason Modal */}
      <LossReasonModal
        isOpen={showLossReasonModal}
        onClose={handleLossReasonCancel}
        onConfirm={handleLossReasonConfirm}
        leadName={formData.name}
      />
    </div>
  );
};

export default LeadFormModal;
